import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { FileText, Upload, Download, CheckCircle2, AlertTriangle, Clock, FileUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const TemplateDocumentiPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const fileInputs = useRef({});

  // Auth: cookie httpOnly (axios.defaults.withCredentials=true)

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/document-templates`);
      setTemplates(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleUpload = async (templateKey, file) => {
    if (!file) return;
    if (!file.name.endsWith('.docx')) {
      toast.error('Solo file .docx sono accettati');
      return;
    }
    setUploading(templateKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/document-templates/${templateKey}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(data.message);
      setFileInputKey(prev => prev + 1);
      fetchTemplates();
    } catch (err) {
      toast.error('Errore durante il caricamento');
    } finally {
      setUploading(null);
    }
  };

  const handleDownload = async (templateKey, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/document-templates/${templateKey}/download`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `${templateKey}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };

  const handleDownloadVariabili = async (templateKey) => {
    try {
      const res = await fetch(`${API_URL}/api/document-templates/${templateKey}/download-variabili`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `schema_variabili_${templateKey}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore download schema variabili'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700" /></div>;
  }

  return (
    <div data-testid="template-documenti-page">
      <div className="page-header">
        <h1 className="text-2xl font-bold">Template Documenti</h1>
        <p className="text-slate-500 text-sm">Gestione template .docx per mandato e contratto di servizio</p>
      </div>

      <div className="page-content space-y-6">
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5 text-slate-600" />
              Template Caricati
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Template</TableHead>
                  <TableHead className="w-24 text-center">Versione</TableHead>
                  <TableHead className="w-28">Dimensione</TableHead>
                  <TableHead className="w-44">Ultimo Aggiornamento</TableHead>
                  <TableHead className="w-40">Aggiornato da</TableHead>
                  <TableHead className="w-48 text-center">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.key} data-testid={`template-${tpl.key}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{tpl.nome}</p>
                          <p className="text-xs text-slate-400">{tpl.filename}</p>
                          {tpl.original_filename && tpl.original_filename !== tpl.filename && (
                            <p className="text-xs text-slate-400">Originale: {tpl.original_filename}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={tpl.versione > 1 ? "default" : "secondary"} className="text-xs">
                        v{tpl.versione || 1}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatBytes(tpl.file_size)}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {tpl.updated_at ? (
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {new Date(tpl.updated_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      ) : (
                        <span className="text-slate-400">Originale</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {tpl.updated_by || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="outline" size="sm" className="text-xs"
                          onClick={() => handleDownload(tpl.key, tpl.filename)}
                          data-testid={`download-template-${tpl.key}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Scarica
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => handleDownloadVariabili(tpl.key)}
                          data-testid={`download-variabili-${tpl.key}`}>
                          <FileText className="w-3.5 h-3.5 mr-1" /> Schema Variabili
                        </Button>
                        <Button variant="default" size="sm" className="text-xs"
                          disabled={uploading === tpl.key}
                          onClick={() => fileInputs.current[tpl.key]?.click()}
                          data-testid={`upload-template-${tpl.key}`}>
                          <Upload className="w-3.5 h-3.5 mr-1" />
                          {uploading === tpl.key ? 'Caricamento...' : 'Aggiorna'}
                        </Button>
                        <input
                          ref={(el) => { fileInputs.current[tpl.key] = el; }}
                          key={`${tpl.key}-${fileInputKey}`}
                          type="file"
                          accept=".docx"
                          style={{ display: 'none' }}
                          onChange={(e) => handleUpload(tpl.key, e.target.files[0])}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <FileUp className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Come funziona</p>
                <ul className="space-y-1 text-blue-700">
                  <li>Carica una nuova versione del template .docx tramite il pulsante "Aggiorna"</li>
                  <li>La versione precedente viene salvata automaticamente come backup</li>
                  <li>I documenti generati useranno sempre l'ultima versione caricata</li>
                  <li>Scarica il template attuale per verificare o modificare</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TemplateDocumentiPage;
